// Related Words modal
import React, { useEffect, useState, useRef } from 'react';
import Matter, { Body, Render, Engine, Composite, Vector, World, Bodies } from 'matter-js';
import useMeasure from 'react-use-measure';
import { ResizeObserver } from '@juggle/resize-observer';
import { randomId, sendAppEvent } from '../../Utils';
import RelatedWordsFeedback from './RelatedWordsFeedback';

const paused = false;

function getLayoutInsets(width, height) {
  return {
    x: Math.max(44, Math.min(96, width * 0.12)),
    y: Math.max(36, Math.min(80, height * 0.08)),
  };
}

function scaleOriginsToInsets(origins, width, height, insets) {
  if (!origins?.length) {
    return [];
  }

  const innerWidth = Math.max(1, width - insets.x * 2);
  const innerHeight = Math.max(1, height - insets.y * 2);

  return origins.map(([x, y]) => [
    insets.x + (x / width) * innerWidth,
    insets.y + (y / height) * innerHeight,
  ]);
}

function getOrbitRadiusScale(width, insets) {
  const innerWidth = width - insets.x * 2;
  if (innerWidth >= 760) {
    return 1;
  }
  return Math.max(0.45, innerWidth / 760);
}

function clampToLayoutBounds(x, y, halfWidth, halfHeight, width, height, insets) {
  const minX = insets.x + halfWidth;
  const maxX = width - insets.x - halfWidth;
  const minY = insets.y + halfHeight;
  const maxY = height - insets.y - halfHeight;

  return [
    Math.min(Math.max(x, minX), maxX),
    Math.min(Math.max(y, minY), maxY),
  ];
}

function copyToClipboard(textToCopy) {
  // navigator clipboard api needs a secure context (https)
  if (navigator.clipboard && window.isSecureContext) {
      // navigator clipboard api method'
      return navigator.clipboard.writeText(textToCopy);
  } else {
      // text area method
      let textArea = document.createElement("textarea");
      textArea.value = textToCopy;
      // make the textarea out of viewport
      textArea.style.position = "fixed";
      textArea.style.left = "-999999px";
      textArea.style.top = "-999999px";
      document.body.appendChild(textArea);
      textArea.focus();
      textArea.select();
      return new Promise((res, rej) => {
          // here the magic happens
          document.execCommand('copy') ? res() : rej();
          textArea.remove();
      });
  }
}

export default ({
  initialWord,
  initialParagraph,
  close,
  documentId,
  insertRelatedWord,
  range,
  relatedWordsStore,
  relativeIndex,
}) => {

  const [word, setWord] = useState(initialWord.toLowerCase().trim());
  const [paragraph, setParagraph] = useState(initialParagraph || initialWord);
  const [anchorsData, setAnchorsData] = useState(null);
  const [selectedTileId, setSelectedTileId] = useState(null);
  const [feedbackOpen, setFeedbackOpen] = useState(false);

  // Matter refs
  const engine = useRef(null);
  const renderLoopId = useRef(null);
  const render = useRef(null);
  const initializedWidth = useRef(null);
  const initializedHeight = useRef(null);

  const positions = useRef(null);
  const tiles = useRef(null); // list of tiles

  const more = useRef(null);
  const copy = useRef(null);
  const clickAgain = useRef(null);

  const wallComposite = useRef(null);

  // DOM refs
  const matterEl = useRef();
  const anchorEls = useRef({}); // parallel structure
  const paragraphEl = useRef(null);
  const mainEl = useRef();
  const moreEl = useRef();
  const copyEl = useRef();
  const clickAgainEl = useRef();

  const [element, bounds] = useMeasure({ polyfill: ResizeObserver });
  const layoutBounds = bounds;

  const MAX_ANCHORS = 4;
  const MAX_WORDS = 20;

  // Escape key closes the modal
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.key !== 'Escape' || e.defaultPrevented) {
        return;
      }

      e.preventDefault();
      e.stopPropagation();

      // Close the nested feedback form first so its draft and the Related
      // Words dialog remain available.
      if (feedbackOpen && anchorsData?.length > 0) {
        setFeedbackOpen(false);
        return;
      }

      close();
    };
    window.addEventListener('keydown', handleKeyDown, true);
    return () => window.removeEventListener('keydown', handleKeyDown, true);
  }, [close, feedbackOpen, anchorsData]);

  // mixpanel
  useEffect(() => {
    sendAppEvent("Related Words - Open", { word: initialWord });
  }, []);

  useEffect(() => {
    let cancelled = false;
    anchorEls.current = {};
    setFeedbackOpen(false);
    setAnchorsData(null);

    relatedWordsStore.get(word).then(clusters => {
      if (cancelled) return;
      if (clusters) {
        // console.log(clusters);

        let words = [];

        for (let cluster of clusters) {
          words.push(cluster.anchor);
          for (let word of cluster.words.map(word => word[0])) {
            words.push(word);
          }
        }

        // console.log(words);
        relatedWordsStore.fetch(words); // prefetch all the words in each cluster

        // console.log(clusters.slice(0, MAX_ANCHORS));

        // temporary dedupe clusters. TODO: take out once data is fixed
        let seen = {};
        let dedupedClusters = [];
        for (let cluster of clusters) {
          if (!seen[cluster.anchor]) {
            dedupedClusters.push(cluster);
            seen[cluster.anchor] = true;
          }
        }

        if (word !== initialWord) {
          sendAppEvent("Related Words - More", { word: word, initialWord: initialWord });
        }

        setAnchorsData(dedupedClusters.slice(0, MAX_ANCHORS));
      } else {
        // not found
        console.log("Related word not found for", word);
        setAnchorsData([]);
      }
    });

    return () => {
      cancelled = true;
    };
  }, [word]);

  // MATTER + DOM
  // rule: always use center coordinate in Matter world. Translate to top/left in css/html world

  useEffect(() => {

    function initialize() {
      console.log("Call to initialize");

      engine.current = Engine.create({
        gravity: { scale: 0 },
        // positionIterations: 24,
      });

      tiles.current = [];

      // create a renderer
      render.current = Render.create({
        element: matterEl.current,
        engine: engine.current,
        options: {
          height: 800,
          width: 800,
        }

      });

      Render.run(render.current);
      if (paused) {
        Render.stop(render.current);
      }
    }

    function destroyEngine() {
      if (renderLoopId.current) {
        cancelAnimationFrame(renderLoopId.current);
        renderLoopId.current = null;
      }

      if (engine.current) {
        removeWalls();
        Render.stop(render.current);
        World.clear(engine.current.world);
        Engine.clear(engine.current);
        engine.current = null;
        render.current.canvas.remove();
        render.current.canvas = null;
        render.current.context = null;
        render.current.textures = {};
        positions.current = null;
        more.current = null;
        clickAgainEl.current = null;
        copyEl.current = null;
      }
    }

    function destroyAll() {
      unselect();
      destroyEngine();
      if (anchorEls.current) {
        anchorEls.current = {};
      }
      initializedWidth.current = null;
      initializedHeight.current = null;
    }

    if (!anchorsData || anchorsData.length === 0) {
      destroyAll();
      return;
    }

    // console.log("anchorEls: ", anchorEls.current);
    // console.log("bounds.width", layoutBounds.width);
    // console.log("anchorEls.current", anchorEls.current);
    // console.log("Object.keys(anchorEls.current).length", Object.keys(anchorEls.current).length);
    // console.log("anchorsData.length", anchorsData.length);

    if (layoutBounds.width > 0 && anchorEls.current && Object.keys(anchorEls.current).length === anchorsData.length) {

      if (engine.current) {
        if (layoutBounds.width === initializedWidth.current && layoutBounds.height === initializedHeight.current) {
          return;
        }
        unselect();
        destroyEngine();
      }

      initialize();
      initializedWidth.current = layoutBounds.width;
      initializedHeight.current = layoutBounds.height;

      const width = layoutBounds.width;
      const height = layoutBounds.height;
      const insets = getLayoutInsets(width, height);
      const radiusScale = getOrbitRadiusScale(width, insets);

      let keys = Object.keys(anchorEls.current);

      let origins;

      // Layouts based on cluster number
      switch (keys.length) {
        case 1:
          origins = [
            [width / 2, height / 5],
          ];
          break;
        case 2:
          origins = [
            [width / 2, height / 5],
            [width / 2, 3 * height / 4],
          ];
          break;
        case 3:
          origins = [
            [width / 5, height / 5],
            [3 * width / 5, height / 5],
            [width / 2 - 100, 3 * height / 4],
          ];
          break;
        case 4:
          origins = [
            [width / 5, height / 5],
            [3 * width / 5, height / 5],
            [width / 5, 3 * height / 4],
            [3 * width / 5, 3 * height / 4],
          ];
          break;

      }

      if (!origins) {
        return () => {
          destroyEngine();
        };
      }

      origins = scaleOriginsToInsets(origins, width, height, insets);

      // console.log("anchors:", keys);

      for (let i = 0; i < keys.length; i++) {
        let key = keys[i];
        let anchorEl = anchorEls.current[key].el;

        let wordKeys = Object.keys(anchorEls.current[key].words);

        let buffer;
        if (wordKeys.length > 8) {
          buffer = 0;
        } else {
          buffer = -25;
        }

        const wordBuffer = 10;

        // calculate the center of the anchor
        let [originX, originY] = clampToLayoutBounds(
          origins[i][0] + anchorEl.offsetWidth / 2,
          origins[i][1] + anchorEl.offsetHeight / 2,
          anchorEl.offsetWidth / 2,
          anchorEl.offsetHeight / 2,
          width,
          height,
          insets,
        );

        // initialize the anchor
        const anchor = createBody({
          x: originX,
          y: originY,
          element: anchorEl,
          word: key,
          rotatable: false,
          bodyOptions: {
            frictionAir: 0.8,
            isSleeping: true,
            label: "anchor",
          },
        });

        tiles.current.push(anchor);

        let theta = 0;
        let count = 0;

        let radiusX = (anchorEl.offsetWidth + buffer) * radiusScale;
        let radiusY = (anchorEl.offsetHeight + buffer) * radiusScale;

        for (let wordKey of wordKeys) {

          let word = anchorEls.current[key].words[wordKey];

          // calculate offsets
          const yOffset = radiusY * Math.cos(theta);
          const xOffset = radiusX * Math.sin(theta);

          const tileHalfW = (word.offsetWidth + 3 + wordBuffer) / 2;
          const tileHalfH = (word.offsetHeight + 3 + wordBuffer) / 2;
          const [x, y] = clampToLayoutBounds(
            originX + xOffset,
            originY + yOffset,
            tileHalfW,
            tileHalfH,
            width,
            height,
            insets,
          );

          let tile = createBody({
            x: x,
            y: y,
            element: word,
            word: wordKey,
            rotatable: false,
            bodyOptions: {
              label: "word",
              inertia: Infinity,
              setMass: 15,
              frictionAir: 0.8,
              // restitution: 0.5,
            },
            width: word.offsetWidth + 3 + wordBuffer,
            height: word.offsetHeight + 3 + wordBuffer,
          });

          // apply a random rotation
          let randomRotation = Math.random() * 0.1 - 0.05;
          word.style.transform = `rotate(${randomRotation}rad)`;

          tiles.current.push(tile);

          theta += Math.PI / 4;
          count++;

          if (count % 8 == 0) {
            theta += Math.PI / 8;
            radiusY += anchorEl.offsetHeight / 1.5;
            radiusX += anchorEl.offsetWidth / 1.5;
          }
          if (count > 8) {
            word.classList.add("outer");
          }
        }
      }

      addWalls();

      for (let tile of tiles.current) {
        Composite.add(engine.current.world, tile.body);
      }

      // The main render loop
      function rerender() {
        if (engine.current) {
          for (let tile of tiles.current) {
            tile.render();
          }
          if (more.current) {
            more.current.render();
          }
          if (copy.current) {
            copy.current.render();
          }
          if (clickAgain.current) {
            clickAgain.current.render();
          }
          Engine.update(engine.current);
          if (!paused) {
            requestAnimationFrame(rerender);
          }
        }
      }

      renderLoopId.current = requestAnimationFrame(rerender);

      return () => {
        unselect();
        destroyEngine();
      };
    }
  }, [anchorsData, anchorEls, paragraphEl, layoutBounds.width, layoutBounds.height]);

  function saveTilePositions() {
    // save the positions
    if (!positions.current) { // only do this once
      positions.current = {};
      for (let t of tiles.current) {
        positions.current[t.body.id] = Object.assign({}, t.body.position);
      }
    }
  }

  function restoreTilePositions() {
    // pop position
    if (!positions.current) {
      throw new Error("Attempting to restore tile positions before saving");
    }

    for (let t of tiles.current) {
      let v = positions.current[t.body.id];
      Body.setPosition(t.body, Vector.create(v.x, v.y));
    }
  }

  function addWalls() {
    if (wallComposite.current) {
      throw new Error("Attempting to add walls when they already exist");
    }

    const width = layoutBounds.width;
    const height = layoutBounds.height;
    const insets = getLayoutInsets(width, height);

    wallComposite.current = Composite.create();
    Composite.add(wallComposite.current, [
      Bodies.rectangle(width / 2, insets.y / 2, width, 10, { isStatic: true }),
      Bodies.rectangle(width / 2, height - insets.y / 2, width, 10, { isStatic: true }),
      Bodies.rectangle(insets.x / 2, height / 2, 10, height, { isStatic: true }),
      Bodies.rectangle(width - insets.x / 2, height / 2, 10, height, { isStatic: true }),
      Bodies.rectangle(width / 2, height / 2, width, 50, { isStatic: true }),
    ]);

    // add walls to prevent tiles from going out
    Composite.add(engine.current.world, wallComposite.current);
  }

  function removeWalls() {
    if (wallComposite.current) {
      Composite.remove(engine.current.world, wallComposite.current);
      wallComposite.current = null;
    }
  }

  // Creates a tracked body+dom object
  const createBody = ({ x, y, element, word, rotatable, bodyOptions, width, height, margin }) => {

    let theWidth, theHeight;
    if (!width) {
      theWidth = element.offsetWidth;
      theHeight = element.offsetHeight;
    } else {
      theWidth = width;
      theHeight = height;
    }

    bodyOptions.chamfer = {
      radius: 25,
    };

    bodyOptions.friction = 0.9;
    bodyOptions.slop = 0.4;

    return {
      id: randomId(5),
      body: Matter.Bodies.rectangle(x, y, theWidth, theHeight, bodyOptions),
      // body: Matter.Bodies.circle(x, y, theWidth / 2, bodyOptions),
      word: word,
      elem: element,
      render() {
        let { x, y } = this.body.position;

        x = Math.round(x - this.elem.offsetWidth / 2);
        x = 2 * Math.round(x / 2);
        y = Math.round(y - this.elem.offsetHeight / 2);

        this.elem.style.top = `${y}px`;
        this.elem.style.left = `${x}px`;
        if (rotatable) {
          this.elem.style.transform = `rotate(${this.body.angle}rad)`;
        }
      }
    };
  };

  function setAnchorBodiesStatic(isStatic) {
    let count = 0;
    if (engine.current) {
      for (let body of engine.current.world.bodies) {
        if (body.label === "anchor") {
          // Body.setStatic(body, isStatic);
          Matter.Sleeping.set(body, isStatic);
          count++;
        }
      }

      // console.log(`Set ${count} anchors to static ${isStatic}`);
    }
  }

  function selectedTile() {
    if (!engine.current) {
      return false;
    }

    return tiles.current.find(tile => tile.selected);
  }

  function unselect() {
    let selected = selectedTile();
    if (!selected) {
      setSelectedTileId(null);
      return;
    }

    if (selected.body.label !== "anchor") {
      Body.scale(selected.body, 0.8, 0.8);
      selected.elem.style.transform = "scale(1)";
    }

    restoreTilePositions();

    selected.selected = false;
    setSelectedTileId(null);
    setAnchorBodiesStatic(true);
    Matter.Sleeping.set(selected.body, false);

    if (more.current) {
      Composite.remove(engine.current.world, more.current.body);
    }

    if (copy.current) {
      Composite.remove(engine.current.world, copy.current.body);
    }

    // Composite.remove(engine.current.world, clickAgain.current.body);

    const safeRemove = (element) => {
      if (mainEl.current && element && element.parentNode === mainEl.current) {
        mainEl.current.removeChild(element);
      }
    };

    safeRemove(moreEl.current);
    safeRemove(copyEl.current);
    safeRemove(clickAgainEl.current);

    more.current = null;
    copy.current = null;
    clickAgainEl.current = null;
    selected.elem.classList.remove("selected");

    addWalls();
  }


  function selectTile(element) {
    if (!engine.current) {
      throw new Error("Attempting to highlight a tile while no engine is running");
    }

    // find the body
    let tile = tiles.current.find(tile => element === tile.elem);
    let selected = selectedTile();

    if (selected == tile) {
      // selected tile again
      insertRelatedWord(tile.word, range);
    } else {
      // selected a different tile
      console.log("Selecting tile", tile);

      unselect(); // first unselect the currently selected tile
      saveTilePositions(); // save positions of all tiles
      removeWalls();

      // determine if we're near an edge, and if so we should reposition the tile so that
      // the menu items will fit
      let tileBounds = tile.elem.getBoundingClientRect();

      // hard coded values for buffer so that menu items will fit
      const rightLeftBuffer = 100;
      const bottomBuffer = 50;
      let xDiff = 0;
      let yDiff = 0;

      if (tileBounds.right + rightLeftBuffer > layoutBounds.right) {
        xDiff = tileBounds.right + rightLeftBuffer - layoutBounds.right;
      }
      if (tileBounds.left - rightLeftBuffer < layoutBounds.left) {
        xDiff = tileBounds.left - rightLeftBuffer - layoutBounds.left;
      }
      if (tileBounds.bottom + bottomBuffer > layoutBounds.bottom) {
        yDiff = tileBounds.bottom + bottomBuffer - layoutBounds.bottom;
      }

      // if we calculate a difference, then we move the tile
      if (xDiff !== 0 || yDiff !== 0) {
        Body.setPosition(tile.body, { x: tile.body.position.x - xDiff, y: tile.body.position.y - yDiff });
      }

      tile.selected = true;
      setSelectedTileId(element.dataset.tileId);
      element.classList.add("selected");

      if (tile.body.label !== "anchor") {
        Body.scale(tile.body, 1.25, 1.25);
        tile.elem.style.transform = "scale(1.25)";
      }

      setAnchorBodiesStatic(false); // set all anchor tiles to be non-static so they can reposition themselves
      Matter.Sleeping.set(tile.body, true);

      let timeoutId;

      let queueTooltip = function () {
        timeoutId = setTimeout(() => {
          if (tile.selected && engine.current) {
            // Add the click again element
            clickAgainEl.current = document.createElement("div");
            clickAgainEl.current.className = "clickAgain";
            clickAgainEl.current.innerHTML = "click again to replace";

            // Figure out how to center the element below the tile
            let bounds = tile.elem.getBoundingClientRect();
            let mainBounds = mainEl.current.getBoundingClientRect();

            mainEl.current.appendChild(clickAgainEl.current);

            const isAnchor = tile.body.label == "anchor";

            const clickAgainSpacing = isAnchor ? 5 : 10;
            clickAgainEl.current.style.top = bounds.bottom - mainBounds.top + clickAgainSpacing + "px";

            // console.log("width", clickAgainEl.current.offsetWidth)

            clickAgainEl.current.style.left = bounds.left - mainBounds.left - (clickAgainEl.current.offsetWidth - bounds.width) / 2 + "px";
          }
        }, 1000);
      };

      queueTooltip();

      tile.elem.onmouseenter = function () {
        if (!timeoutId) {
            queueTooltip();
        }
      };

      tile.elem.onmouseleave = function () {
        if (timeoutId !== null) {
          clearTimeout(timeoutId);
          timeoutId = null;
        }
        if (clickAgainEl.current) {
          mainEl.current.removeChild(clickAgainEl.current);
          clickAgainEl.current = null;
        }
      };

      relatedWordsStore.get(tile.word).then(related => {
        if (!mainEl.current) {
          return;
        }

        if (related) {
          // Add elements
          moreEl.current = document.createElement("button");
          moreEl.current.type = "button";
          moreEl.current.className = "more";
          moreEl.current.innerHTML = "MORE";
          moreEl.current.onclick = e => {
            anchorEls.current = {};
            setFeedbackOpen(false);
            setWord(tile.word);
            setParagraph(null);
          };
          mainEl.current.appendChild(moreEl.current);

          more.current = createBody({
            x: tile.body.bounds.min.x - moreEl.current.offsetWidth / 2,
            y: tile.body.position.y,
            element: moreEl.current,
            bodyOptions: {
              isStatic: true,
            }
          });
        } else {
          moreEl.current = null;
        }

        copyEl.current = document.createElement("button");
        copyEl.current.type = "button";
        copyEl.current.className = "copy";
        copyEl.current.innerHTML = "COPY";
        mainEl.current.appendChild(copyEl.current);
        copyEl.current.onclick = (e) => {
          // navigator.clipboard.writeText(tile.word);
          copyToClipboard(tile.word);

          copyEl.current.classList.add("copied");

          sendAppEvent("Related Words - Copy", { word: word, initialWord: initialWord });

          setTimeout(() => {
            if (copyEl.current) {
              copyEl.current.classList.remove("copied");
            }
          }, 1000);
          e.stopPropagation();
        };

        copy.current = createBody({
          x: tile.body.bounds.max.x + copyEl.current.offsetWidth / 2,
          y: tile.body.position.y,
          element: copyEl.current,
          bodyOptions: {
            isStatic: true,
          }
        });

        if (more.current) {
          Composite.add(engine.current.world, [more.current.body]);
        }

        Composite.add(engine.current.world, [copy.current.body]);
      });

    }
  }

  let trimmedParagraph;

  if (paragraph) {
    const window = 25;
    const center = relativeIndex || paragraph.indexOf(word);
    let right = paragraph.substring(center + word.length, center + window);
    let left = paragraph.substring(center > window ? center - window : 0, center);

    if (center > window) {
      left = "..." + left;
    }

    if (center + window < paragraph.length) {
      right += "...";
    }

    trimmedParagraph = <>{left} <strong>{word}</strong> {right}</>;
  } else {
    trimmedParagraph = <strong>{word}</strong>;
  }

  // console.log("trimmedPara", trimmedParagraph);

  const trapDialogFocus = e => {
    if (e.key !== "Tab") {
      return;
    }

    const focusable = Array.from(e.currentTarget.querySelectorAll(
      'button:not([disabled]), textarea:not([disabled]), input:not([disabled]), select:not([disabled]), [href], [tabindex]:not([tabindex="-1"])'
    ));
    if (focusable.length === 0) {
      return;
    }

    const first = focusable[0];
    const last = focusable[focusable.length - 1];
    if (e.shiftKey && document.activeElement === first) {
      e.preventDefault();
      last.focus();
    } else if (!e.shiftKey && document.activeElement === last) {
      e.preventDefault();
      first.focus();
    }
  };

  return (
    <div
      ref={element}
      id="related-words"
      key="related"
      role="dialog"
      aria-modal="true"
      aria-label={`Related words for ${word}`}
      onKeyDown={trapDialogFocus}
      onClick={e => {
      if (selectedTile()) {
        unselect();
      } else {
        close();
      }
    }}>
      <button
        type="button"
        className="close"
        aria-label="Close related words"
        autoFocus
        onClick={e => { e.stopPropagation(); close(); }}
      >
        &#x2715;
      </button>

      {anchorsData === null && (
        <div className="related-words-loading" role="status" aria-live="polite">Loading...</div>
      )}

      {anchorsData && anchorsData.length === 0 && (
        <div className="related-words-empty" role="status">No related words found.</div>
      )}

      {anchorsData && anchorsData.length > 0 &&
        <>
          <div ref={mainEl} className="main" key = "anchors">
            <div className="paragraph" ref={paragraphEl}>{trimmedParagraph}</div>

            {/* {selectedTileId &&
              <>
                <a ref={moreRef} className="more">More</a>
                <a ref={copyRef} className="copy">Copy</a>
                <div ref={clickAgainRef} className="clickAgain">click again to replace</div>
              </>
            } */}

            {anchorsData.map((anchor, i) => {
              const anchorTileId = `anchor:${anchor.anchor}`;
              return (
                <div key = {"anchor" + i}>
                  <button
                    type="button"
                    ref={el => {
                      if (!el) {
                        return;
                      }

                      if (!anchorEls.current[anchor.anchor]) {
                        anchorEls.current[anchor.anchor] = {};
                      }
                      anchorEls.current[anchor.anchor].el = el;
                    }}
                    className="anchor tile"
                    key={anchor.anchor}
                    data-tile-id={anchorTileId}
                    aria-pressed={selectedTileId === anchorTileId}
                    onClick={e => {
                      selectTile(e.currentTarget);
                      e.stopPropagation();
                    }}
                  >{anchor.anchor}</button>
                  {anchor.words.slice(0,MAX_WORDS).map((word,i) => {
                    const wordTileId = `word:${anchor.anchor}:${word[0]}`;
                    return (
                      <button
                        type="button"
                        className="tile"
                        key={`${anchor.anchor}-${word[0]}`}
                        data-tile-id={wordTileId}
                        aria-pressed={selectedTileId === wordTileId}
                        ref={el => {
                          if (!el) {
                            return;
                          }
                          if (!anchorEls.current[anchor.anchor]) {
                            anchorEls.current[anchor.anchor] = {};
                          }
                          if (!anchorEls.current[anchor.anchor].words) {
                            anchorEls.current[anchor.anchor].words = {};
                          }
                          anchorEls.current[anchor.anchor].words[word[0]] = el;
                        }}
                        onClick={e => {
                          selectTile(e.currentTarget);
                          e.stopPropagation();
                        }}
                      >{word[0]}</button>
                    );
                  })}

                </div>
              );
            })}
          </div>

          <div ref={matterEl} className="matter"></div>
          {/* <div className="menu">
            <a onClick={e => {
              for (let tile of tiles.current) {
                console.log(tile);
              }

              console.log("Total", tiles.current.length);

              for (let body of engine.current.world.bodies) {
                console.log(body);
              }

              console.log("Total Bodies on World", engine.current.world.bodies.length);
            }}>List</a>
          </div>  */}

          <RelatedWordsFeedback
            key={word}
            word={word}
            open={feedbackOpen}
            onOpenChange={setFeedbackOpen}
          />
        </>
      }
    </div>
  );
};
